import React, { useEffect, useState } from 'react';
import { Card } from '@tremor/react';
import { VideoCameraIcon, UsersIcon } from '@heroicons/react/24/outline';
import { getConferenceRecordingsForAnalysis } from '../../Requests/NewAnalysisRequests';

/**
 * Shows the video-conference recordings backing an analysis: the group
 * composite (whole call) plus each participant's individual recording.
 * Specific to analyses created from a video conference; renders nothing when
 * the analysis has no associated conference recordings.
 */
export default function ConferenceRecordingsCard({ analysisUuid }) {
    const [composite, setComposite] = useState(null);
    const [individuals, setIndividuals] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        if (!analysisUuid) return;

        getConferenceRecordingsForAnalysis(analysisUuid)
            .then((res) => {
                if (!active) return;
                setComposite(res.data.composite || null);
                setIndividuals(res.data.individuals || []);
            })
            .catch(() => {
                if (active) {
                    setComposite(null);
                    setIndividuals([]);
                }
            })
            .finally(() => active && setLoading(false));

        return () => { active = false; };
    }, [analysisUuid]);

    // Nothing to show if this analysis isn't backed by conference recordings.
    if (loading || (!composite && individuals.length === 0)) {
        return null;
    }

    return (
        <Card className="w-full">
            <div className="flex items-center gap-2 mb-4">
                <VideoCameraIcon className="h-5 w-5 text-iavisus-100" />
                <h3 className="text-lg font-semibold text-gray-800">Conference Recordings</h3>
            </div>

            {composite && (
                <div className="mb-6">
                    <p className="text-sm font-medium text-gray-600 mb-2">Full conference (composite)</p>
                    <video
                        controls
                        preload="metadata"
                        className="w-full rounded-xl bg-black max-h-[480px]"
                        src={composite.url}
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>
            )}

            {individuals.length > 0 && (
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <UsersIcon className="h-4 w-4 text-gray-500" />
                        <p className="text-sm font-medium text-gray-600">
                            Per participant ({individuals.length})
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {individuals.map((rec) => (
                            <div key={rec.id} className="flex flex-col">
                                <p className="text-xs text-gray-500 mb-1 truncate">
                                    {rec.participant || rec.display_name}
                                </p>
                                <video
                                    controls
                                    preload="metadata"
                                    className="w-full rounded-lg bg-black"
                                    src={rec.url}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
}
